"use client";

import { Fragment } from "react";
import {
  MessageCircleHeart,
  MapPin,
  CalendarClock,
  Users,
  UserPen,
  CreditCard,
  CheckCheck,
  FileText,
} from "lucide-react";

export interface BookingStep {
  id: string;
  number: string;
  label: string;
  icon: React.ReactNode;
}

// Service theme type for brand colors
export type ServiceTheme = "counseling" | "lifeCoaching" | "occupationalTherapy" | "psychology";

// Brand color configuration for each service
const themeColors: Record<ServiceTheme, { primary: string; primaryClass: string; textClass: string }> = {
  counseling: {
    primary: "#896deb", // Purple
    primaryClass: "bg-[#896deb]",
    textClass: "text-[#896deb]",
  },
  lifeCoaching: {
    primary: "#DDA500", // Yellow/Gold
    primaryClass: "bg-[#DDA500]",
    textClass: "text-[#DDA500]",
  },
  occupationalTherapy: {
    primary: "#C01610", // Red
    primaryClass: "bg-red",
    textClass: "text-red",
  },
  psychology: {
    primary: "#C01610", // Red
    primaryClass: "bg-red",
    textClass: "text-red",
  },
};

interface BookingStepperProps {
  currentStep: number;
  steps?: BookingStep[];
  allCompleted?: boolean;
  // New prop for NDIS flow
  includeNdisStep?: boolean;
  // Theme prop for brand colors per service (replaces useRedTheme)
  serviceTheme?: ServiceTheme;
  // Legacy prop for backwards compatibility - maps to psychology/OT theme
  useRedTheme?: boolean;
  // Callback when a completed step is clicked for back-navigation
  onStepClick?: (stepIndex: number) => void;
}

// Default steps (6 steps) - for Counselling, Life Coaching & Allied Health
const defaultSteps: BookingStep[] = [
  {
    id: "support-options",
    number: "01",
    label: "Support Options",
    icon: <MessageCircleHeart className="size-5" />,
  },
  {
    id: "service-location",
    number: "02",
    label: "Service Location",
    icon: <MapPin className="size-5" />,
  },
  {
    id: "date-time",
    number: "03",
    label: "Date & Time",
    icon: <CalendarClock className="size-5" />,
  },
  {
    id: "practitioner",
    number: "04",
    label: "Practitioner",
    icon: <Users className="size-5" />,
  },
  {
    id: "personal-details",
    number: "05",
    label: "Personal Details",
    icon: <UserPen className="size-5" />,
  },
  {
    id: "payment",
    number: "06",
    label: "Payment",
    icon: <CreditCard className="size-5" />,
  },
];

// NDIS steps (7 steps) - for Occupational Therapy and Psychology
const ndisSteps: BookingStep[] = [
  {
    id: "support-options",
    number: "01",
    label: "Support Options",
    icon: <MessageCircleHeart className="size-5" />,
  },
  {
    id: "funding-support",
    number: "02",
    label: "Funding Support",
    icon: <FileText className="size-5" />,
  },
  {
    id: "service-location",
    number: "03",
    label: "Service Location",
    icon: <MapPin className="size-5" />,
  },
  {
    id: "date-time",
    number: "04",
    label: "Date & Time",
    icon: <CalendarClock className="size-5" />,
  },
  {
    id: "practitioner",
    number: "05",
    label: "Practitioner",
    icon: <Users className="size-5" />,
  },
  {
    id: "personal-details",
    number: "06",
    label: "Personal Details",
    icon: <UserPen className="size-5" />,
  },
  {
    id: "payment",
    number: "07",
    label: "Payment",
    icon: <CreditCard className="size-5" />,
  },
];

const StepIndicator = ({
  step,
  isActive,
  isCompleted,
  allCompleted,
  themeColor,
  onStepClick,
  stepIndex,
}: {
  step: BookingStep;
  isActive: boolean;
  isCompleted: boolean;
  allCompleted?: boolean;
  themeColor: { primary: string; primaryClass: string; textClass: string };
  onStepClick?: (stepIndex: number) => void;
  stepIndex: number;
}) => {
  // Determine if this step should show checkmark (completed but not active, or allCompleted)
  const showCheckmark = allCompleted || (isCompleted && !isActive);
  const isClickable = !allCompleted && isCompleted && !isActive && !!onStepClick;

  return (
    <div
      className={`relative flex shrink-0 items-center${isClickable ? " cursor-pointer" : ""}`}
      onClick={isClickable ? () => onStepClick!(stepIndex) : undefined}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-label={isClickable ? `Go back to ${step.label}` : undefined}
      onKeyDown={
        isClickable
          ? (e) => e.key === "Enter" && onStepClick!(stepIndex)
          : undefined
      }
    >
      {/* Step Pill */}
      <div
        className={`relative flex shrink-0 items-center rounded-full border-2 border-white p-1 md:border-4 ${isClickable ? " transition-opacity hover:opacity-80" : ""}`}
        style={{
          boxShadow: "inset 0px 0px 20px 0px rgba(255,255,255,0.5)",
          backgroundColor: themeColor.primary,
        }}
      >
        {/* Inner container - checkmark for completed steps, number + icon for active/future */}
        {showCheckmark ? (
          <div className="flex items-center rounded-full border border-white bg-white/10">
            <div className="flex size-8 items-center justify-center p-1.5 md:size-[45px] md:p-2.5">
              <CheckCheck className="size-4 text-white md:size-5" />
            </div>
          </div>
        ) : (
          <>
            {/* Inner container with number + icon */}
            <div className="flex items-center rounded-full border border-white/80 bg-white/10">
              {/* Number circle */}
              <div className="flex size-8 items-center justify-center rounded-full bg-white md:size-[40px]">
                <span
                  className="font-onest text-sm font-bold md:text-xl"
                  style={{ color: themeColor.primary }}
                >
                  {step.number}
                </span>
              </div>

              {/* Icon - hidden on mobile */}
              <div className="hidden size-[36px] items-center justify-center p-2.5 text-white md:flex">
                {step.icon}
              </div>
            </div>

            {/* Label - only shown when active on larger screens */}
            {isActive && (
              <div className="hidden items-center justify-center p-2.5 lg:flex">
                <span className="whitespace-nowrap font-onest text-base font-medium text-white">
                  {step.label}
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Connector line component - consistent width via grid
const Connector = ({
  isCompleted,
  themeColor,
}: {
  isCompleted: boolean;
  themeColor: { primary: string };
}) => (
  <div
    className="h-1.5 w-full md:h-2.5"
    style={{
      backgroundColor: themeColor.primary,
      opacity: isCompleted ? 1 : 0.3,
    }}
  />
);

// Default theme color (blue) for backwards compatibility
const defaultThemeColor = {
  primary: "#3B82F6", // v3-primary blue
  primaryClass: "bg-v3-primary",
  textClass: "text-v3-primary",
};

const BookingStepper = ({
  currentStep,
  steps,
  allCompleted = false,
  includeNdisStep = false,
  serviceTheme,
  useRedTheme = false,
  onStepClick,
}: BookingStepperProps) => {
  // Use provided steps, or NDIS steps if includeNdisStep is true, otherwise default steps
  const displaySteps = steps || (includeNdisStep ? ndisSteps : defaultSteps);

  // Resolve theme color: serviceTheme takes precedence, then useRedTheme for backwards compatibility
  const themeColor = serviceTheme
    ? themeColors[serviceTheme]
    : useRedTheme
    ? themeColors.psychology // Red theme maps to psychology/OT color
    : defaultThemeColor;

  // Calculate grid template: alternating step indicators and connectors
  // Steps get auto width, connectors get equal 1fr each
  const gridColumns = displaySteps
    .map((_, i) => (i < displaySteps.length - 1 ? "auto 1fr" : "auto"))
    .join(" ");

  return (
    <nav
      className="grid w-full items-center"
      style={{ gridTemplateColumns: gridColumns }}
      aria-label="Booking progress"
      role="progressbar"
      aria-valuenow={allCompleted ? displaySteps.length : currentStep + 1}
      aria-valuemin={1}
      aria-valuemax={displaySteps.length}
    >
      {displaySteps.map((step, index) => (
        <Fragment key={step.id}>
          <StepIndicator
            step={step}
            isActive={!allCompleted && index === currentStep}
            isCompleted={allCompleted || index < currentStep}
            allCompleted={allCompleted}
            themeColor={themeColor}
            onStepClick={onStepClick}
            stepIndex={index}
          />
          {index < displaySteps.length - 1 && (
            <Connector
              isCompleted={allCompleted || index < currentStep}
              themeColor={themeColor}
            />
          )}
        </Fragment>
      ))}
    </nav>
  );
};

export default BookingStepper;
